import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  ShieldCheck, 
  Users, 
  Plus, 
  Search,
  PoundSterling,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  TrendingUp,
  Calendar,
  Mail,
  Phone,
  MapPin,
  Home,
  RefreshCw,
  Send,
  Receipt,
  Eye,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PHSMembersPage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [members, setMembers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialogs
  const [showNewMemberDialog, setShowNewMemberDialog] = useState(false);
  const [showNewTaskDialog, setShowNewTaskDialog] = useState(false);
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);

  // Form state
  const [newMember, setNewMember] = useState({
    name: '', email: '', phone: '', dob: '',
    park_name: '', plot_number: '', address: '', postcode: '',
    plan: 'standard'
  });

  const [newTask, setNewTask] = useState({
    member_id: '', title: '', description: '', category: '', priority: 'standard', property_area: ''
  });

  const [quoteItems, setQuoteItems] = useState([{ description: '', contractor_cost: '', quantity: 1 }]);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/phs/members/dashboard`, { headers });
      setDashboard(res.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    }
  }, []);

  const loadMembers = useCallback(async () => {
    try {
      const url = searchQuery 
        ? `${API}/api/phs/members?search=${encodeURIComponent(searchQuery)}`
        : `${API}/api/phs/members`;
      const res = await axios.get(url, { headers });
      setMembers(res.data.members || []);
    } catch (error) {
      console.error('Error loading members:', error);
    }
  }, [searchQuery]);

  const loadTasks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/phs/members/tasks/list`, { headers });
      setTasks(res.data.tasks || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  }, []);

  const loadQuotes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/phs/members/quotes/list`, { headers });
      setQuotes(res.data.quotes || []);
    } catch (error) {
      console.error('Error loading quotes:', error);
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([loadDashboard(), loadMembers(), loadTasks(), loadQuotes()]);
      setLoading(false);
    };
    loadAll();
  }, [loadDashboard, loadMembers, loadTasks, loadQuotes]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      loadMembers();
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, loadMembers]);

  const handleCreateMember = async () => {
    try {
      await axios.post(`${API}/api/phs/members`, {
        personal_details: {
          name: newMember.name,
          email: newMember.email,
          phone: newMember.phone,
          dob: newMember.dob || null
        },
        property: {
          park_name: newMember.park_name,
          plot_number: newMember.plot_number,
          address: newMember.address,
          postcode: newMember.postcode
        },
        membership: {
          plan: newMember.plan
        }
      }, { headers });
      
      toast.success('Member created successfully');
      setShowNewMemberDialog(false);
      setNewMember({ name: '', email: '', phone: '', dob: '', park_name: '', plot_number: '', address: '', postcode: '', plan: 'standard' });
      loadMembers();
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create member');
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.member_id) {
      toast.error('Please select a member');
      return;
    }
    try {
      await axios.post(`${API}/api/phs/members/tasks`, newTask, { headers });
      toast.success('Task created successfully');
      setShowNewTaskDialog(false);
      setNewTask({ member_id: '', title: '', description: '', category: '', priority: 'standard', property_area: '' });
      loadTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  const handleCreateQuote = async () => {
    if (!selectedTask) return;
    
    const validItems = quoteItems.filter(i => i.description && i.contractor_cost);
    if (validItems.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }

    try {
      await axios.post(`${API}/api/phs/members/tasks/${selectedTask.task_id}/quote`, {
        task_id: selectedTask.task_id,
        line_items: validItems.map(i => ({
          description: i.description,
          contractor_cost: parseFloat(i.contractor_cost),
          quantity: parseInt(i.quantity) || 1
        })),
        valid_days: 30
      }, { headers });
      
      toast.success('Quote created successfully');
      setShowQuoteDialog(false);
      setQuoteItems([{ description: '', contractor_cost: '', quantity: 1 }]);
      loadTasks();
      loadQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quote');
    }
  };

  const handleSendQuote = async (quoteId) => {
    try {
      const res = await axios.post(`${API}/api/phs/members/quotes/${quoteId}/send`, {
        send_email: true,
        send_whatsapp: true
      }, { headers });
      
      toast.success(`Quote sent to ${res.data.sent_to}`);
      loadQuotes();
      loadTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send quote');
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      new: { color: 'bg-blue-100 text-blue-700', label: 'New' },
      reviewing: { color: 'bg-amber-100 text-amber-700', label: 'Reviewing' },
      quote_drafted: { color: 'bg-purple-100 text-purple-700', label: 'Quote Drafted' },
      quote_sent: { color: 'bg-indigo-100 text-indigo-700', label: 'Quote Sent' },
      accepted: { color: 'bg-teal-100 text-teal-700', label: 'Accepted' },
      awaiting_payment: { color: 'bg-orange-100 text-orange-700', label: 'Awaiting Payment' },
      paid: { color: 'bg-green-100 text-green-700', label: 'Paid' },
      scheduled: { color: 'bg-cyan-100 text-cyan-700', label: 'Scheduled' },
      in_progress: { color: 'bg-yellow-100 text-yellow-700', label: 'In Progress' },
      completed: { color: 'bg-emerald-100 text-emerald-700', label: 'Completed' },
      closed: { color: 'bg-slate-100 text-slate-600', label: 'Closed' },
      draft: { color: 'bg-gray-100 text-gray-600', label: 'Draft' },
      sent: { color: 'bg-blue-100 text-blue-700', label: 'Sent' },
      expired: { color: 'bg-red-100 text-red-700', label: 'Expired' },
      active: { color: 'bg-green-100 text-green-700', label: 'Active' },
    };
    const config = configs[status] || { color: 'bg-gray-100 text-gray-600', label: status };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const calculateQuoteTotal = () => {
    const contractor = quoteItems.reduce((sum, item) => {
      return sum + (parseFloat(item.contractor_cost) || 0) * (parseInt(item.quantity) || 1);
    }, 0);
    const margin = contractor * 0.25;
    return {
      contractor: contractor.toFixed(2),
      margin: margin.toFixed(2),
      client: (contractor + margin).toFixed(2)
    };
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-6" data-testid="phs-members-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ShieldCheck className="h-6 w-6 text-teal-600" />
              PHS Members
            </h1>
            <p className="text-gray-500 text-sm mt-1">Membership Operations Engine</p>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => setShowNewTaskDialog(true)}
              data-testid="new-task-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              New Task
            </Button>
            <Button 
              className="bg-teal-600 hover:bg-teal-700"
              onClick={() => setShowNewMemberDialog(true)}
              data-testid="new-member-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              New Member
            </Button>
          </div>
        </div>

        {/* Dashboard Metrics */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            <Card className="bg-gradient-to-br from-teal-50 to-white">
              <CardContent className="p-3 text-center">
                <Users className="h-5 w-5 mx-auto text-teal-600" />
                <div className="text-2xl font-bold text-teal-700">{dashboard.active_members}</div>
                <div className="text-xs text-gray-500">Active Members</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-orange-50 to-white">
              <CardContent className="p-3 text-center">
                <Clock className="h-5 w-5 mx-auto text-orange-600" />
                <div className="text-2xl font-bold text-orange-700">{dashboard.awaiting_payment}</div>
                <div className="text-xs text-gray-500">Awaiting Payment</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-indigo-50 to-white">
              <CardContent className="p-3 text-center">
                <FileText className="h-5 w-5 mx-auto text-indigo-600" />
                <div className="text-2xl font-bold text-indigo-700">{dashboard.quotes_sent}</div>
                <div className="text-xs text-gray-500">Quotes Sent</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-50 to-white">
              <CardContent className="p-3 text-center">
                <TrendingUp className="h-5 w-5 mx-auto text-green-600" />
                <div className="text-2xl font-bold text-green-700">{dashboard.conversion_rate}%</div>
                <div className="text-xs text-gray-500">Conversion</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-50 to-white">
              <CardContent className="p-3 text-center">
                <PoundSterling className="h-5 w-5 mx-auto text-emerald-600" />
                <div className="text-2xl font-bold text-emerald-700">£{dashboard.revenue_this_month.toLocaleString()}</div>
                <div className="text-xs text-gray-500">Revenue (Month)</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-50 to-white">
              <CardContent className="p-3 text-center">
                <Receipt className="h-5 w-5 mx-auto text-amber-600" />
                <div className="text-2xl font-bold text-amber-700">£{dashboard.outstanding_amount.toLocaleString()}</div>
                <div className="text-xs text-gray-500">Outstanding</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-50 to-white">
              <CardContent className="p-3 text-center">
                <AlertCircle className="h-5 w-5 mx-auto text-blue-600" />
                <div className="text-2xl font-bold text-blue-700">{dashboard.open_tasks}</div>
                <div className="text-xs text-gray-500">Open Tasks</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-red-50 to-white">
              <CardContent className="p-3 text-center">
                <Calendar className="h-5 w-5 mx-auto text-red-600" />
                <div className="text-2xl font-bold text-red-700">{dashboard.renewals_due}</div>
                <div className="text-xs text-gray-500">Renewals Due</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="members">Members</TabsTrigger>
            <TabsTrigger value="tasks">Tasks</TabsTrigger>
            <TabsTrigger value="quotes">Quotes</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-4">
            <div className="grid lg:grid-cols-2 gap-4">
              {/* Recent Tasks */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Recent Tasks</CardTitle>
                </CardHeader>
                <CardContent>
                  {tasks.slice(0, 5).map((task) => (
                    <div key={task.task_id} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div>
                        <div className="font-medium text-sm">{task.title}</div>
                        <div className="text-xs text-gray-500">{task.member_name} • {task.category}</div>
                      </div>
                      {getStatusBadge(task.status)}
                    </div>
                  ))}
                  {tasks.length === 0 && (
                    <p className="text-gray-500 text-sm text-center py-4">No tasks yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Quotes Awaiting Action */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Quotes Awaiting Action</CardTitle>
                </CardHeader>
                <CardContent>
                  {quotes.filter(q => q.status === 'draft').slice(0, 5).map((quote) => (
                    <div key={quote.quote_id} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div>
                        <div className="font-medium text-sm">{quote.quote_id}</div>
                        <div className="text-xs text-gray-500">{quote.member_name} • £{quote.client_total}</div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => handleSendQuote(quote.quote_id)}>
                        <Send className="h-3 w-3 mr-1" />
                        Send
                      </Button>
                    </div>
                  ))}
                  {quotes.filter(q => q.status === 'draft').length === 0 && (
                    <p className="text-gray-500 text-sm text-center py-4">No quotes pending approval</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members" className="space-y-4">
            <div className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search members by name, email, postcode..."
                  className="pl-10"
                  data-testid="search-members-input"
                />
              </div>
            </div>

            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Member ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((member) => (
                    <TableRow key={member.id || member.member_id}>
                      <TableCell className="font-mono text-sm">{member.member_id}</TableCell>
                      <TableCell>
                        <div className="font-medium">{member.personal_details?.name}</div>
                        <div className="text-xs text-gray-500">{member.personal_details?.email}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{member.property?.park_name}</div>
                        <div className="text-xs text-gray-500">{member.property?.postcode}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{member.membership?.plan || 'standard'}</Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(member.status)}</TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks" className="space-y-4">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task ID</TableHead>
                    <TableHead>Member</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tasks.map((task) => (
                    <TableRow key={task.task_id}>
                      <TableCell className="font-mono text-sm">{task.task_id}</TableCell>
                      <TableCell>
                        <div className="font-medium">{task.member_name}</div>
                        <div className="text-xs text-gray-500">{task.member_id}</div>
                      </TableCell>
                      <TableCell className="max-w-48 truncate">{task.title}</TableCell>
                      <TableCell className="capitalize">{task.category}</TableCell>
                      <TableCell>{getStatusBadge(task.status)}</TableCell>
                      <TableCell>
                        {task.status === 'new' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setSelectedTask(task);
                              setShowQuoteDialog(true);
                            }}
                          >
                            <FileText className="h-3 w-3 mr-1" />
                            Create Quote
                          </Button>
                        )}
                        {task.status === 'quote_drafted' && (
                          <Button 
                            size="sm" 
                            className="bg-teal-600 hover:bg-teal-700"
                            onClick={() => handleSendQuote(task.quote_id)}
                          >
                            <Send className="h-3 w-3 mr-1" />
                            Send Quote
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes" className="space-y-4">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Quote ID</TableHead>
                    <TableHead>Member</TableHead>
                    <TableHead>Contractor Cost</TableHead>
                    <TableHead>PHS Margin</TableHead>
                    <TableHead>Client Price</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quotes.map((quote) => (
                    <TableRow key={quote.quote_id}>
                      <TableCell className="font-mono text-sm">{quote.quote_id}</TableCell>
                      <TableCell>
                        <div className="font-medium">{quote.member_name}</div>
                      </TableCell>
                      <TableCell className="text-gray-500">£{quote.contractor_total?.toFixed(2)}</TableCell>
                      <TableCell className="text-teal-600 font-medium">£{quote.phs_margin_total?.toFixed(2)}</TableCell>
                      <TableCell className="font-bold">£{quote.client_total?.toFixed(2)}</TableCell>
                      <TableCell>{getStatusBadge(quote.status)}</TableCell>
                      <TableCell>
                        {quote.status === 'draft' && (
                          <Button 
                            size="sm" 
                            className="bg-teal-600 hover:bg-teal-700"
                            onClick={() => handleSendQuote(quote.quote_id)}
                          >
                            <Send className="h-3 w-3 mr-1" />
                            Send
                          </Button>
                        )}
                        {quote.status === 'sent' && (
                          <Badge variant="outline">Awaiting Response</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>

        {/* New Member Dialog */}
        <Dialog open={showNewMemberDialog} onOpenChange={setShowNewMemberDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add New Member</DialogTitle>
              <DialogDescription>Create a new PHS member profile</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={newMember.name}
                    onChange={(e) => setNewMember({...newMember, name: e.target.value})}
                    placeholder="John Smith"
                    data-testid="member-name-input"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={newMember.email}
                    onChange={(e) => setNewMember({...newMember, email: e.target.value})}
                    placeholder="john@example.com"
                    data-testid="member-email-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={newMember.phone}
                    onChange={(e) => setNewMember({...newMember, phone: e.target.value})}
                    placeholder="07123 456789"
                  />
                </div>
                <div>
                  <Label>Membership Plan</Label>
                  <Select value={newMember.plan} onValueChange={(v) => setNewMember({...newMember, plan: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                      <SelectItem value="emergency_only">Emergency Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                  <Home className="h-4 w-4" /> Property Details
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Park Name *</Label>
                    <Input
                      value={newMember.park_name}
                      onChange={(e) => setNewMember({...newMember, park_name: e.target.value})}
                      placeholder="Sunnyvale Park"
                    />
                  </div>
                  <div>
                    <Label>Plot Number</Label>
                    <Input
                      value={newMember.plot_number}
                      onChange={(e) => setNewMember({...newMember, plot_number: e.target.value})}
                      placeholder="42"
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <Label>Address</Label>
                  <Input
                    value={newMember.address}
                    onChange={(e) => setNewMember({...newMember, address: e.target.value})}
                    placeholder="Full property address"
                  />
                </div>
                <div className="mt-3">
                  <Label>Postcode *</Label>
                  <Input
                    value={newMember.postcode}
                    onChange={(e) => setNewMember({...newMember, postcode: e.target.value})}
                    placeholder="AB12 3CD"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewMemberDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleCreateMember}
                disabled={!newMember.name || !newMember.email || !newMember.park_name || !newMember.postcode}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="confirm-create-member-btn"
              >
                Create Member
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* New Task Dialog */}
        <Dialog open={showNewTaskDialog} onOpenChange={setShowNewTaskDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Member Task</DialogTitle>
              <DialogDescription>Create a new work request for a member</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Select Member *</Label>
                <Select value={newTask.member_id} onValueChange={(v) => setNewTask({...newTask, member_id: v})}>
                  <SelectTrigger data-testid="task-member-select">
                    <SelectValue placeholder="Choose a member..." />
                  </SelectTrigger>
                  <SelectContent>
                    {members.map((m) => (
                      <SelectItem key={m.member_id} value={m.member_id}>
                        {m.personal_details?.name} ({m.member_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Task Title *</Label>
                <Input
                  value={newTask.title}
                  onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                  placeholder="e.g., Leaking roof repair"
                  data-testid="task-title-input"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                  placeholder="Detailed description of the work required..."
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category *</Label>
                  <Select value={newTask.category} onValueChange={(v) => setNewTask({...newTask, category: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="plumbing">Plumbing</SelectItem>
                      <SelectItem value="electrical">Electrical</SelectItem>
                      <SelectItem value="roofing">Roofing</SelectItem>
                      <SelectItem value="heating">Heating</SelectItem>
                      <SelectItem value="structural">Structural</SelectItem>
                      <SelectItem value="external">External Works</SelectItem>
                      <SelectItem value="general">General Maintenance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={newTask.priority} onValueChange={(v) => setNewTask({...newTask, priority: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="emergency">Emergency</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="planned">Planned</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewTaskDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleCreateTask}
                disabled={!newTask.member_id || !newTask.title || !newTask.category}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="confirm-create-task-btn"
              >
                Create Task
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Quote Creation Dialog */}
        <Dialog open={showQuoteDialog} onOpenChange={setShowQuoteDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Quote</DialogTitle>
              <DialogDescription>
                {selectedTask?.title} - {selectedTask?.member_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Line Items */}
              <div className="space-y-3">
                <Label>Line Items</Label>
                {quoteItems.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2">
                    <div className="col-span-6">
                      <Input
                        value={item.description}
                        onChange={(e) => {
                          const updated = [...quoteItems];
                          updated[idx].description = e.target.value;
                          setQuoteItems(updated);
                        }}
                        placeholder="Description"
                      />
                    </div>
                    <div className="col-span-3">
                      <Input
                        type="number"
                        value={item.contractor_cost}
                        onChange={(e) => {
                          const updated = [...quoteItems];
                          updated[idx].contractor_cost = e.target.value;
                          setQuoteItems(updated);
                        }}
                        placeholder="Contractor £"
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => {
                          const updated = [...quoteItems];
                          updated[idx].quantity = e.target.value;
                          setQuoteItems(updated);
                        }}
                        placeholder="Qty"
                        min={1}
                      />
                    </div>
                    <div className="col-span-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setQuoteItems(quoteItems.filter((_, i) => i !== idx))}
                        disabled={quoteItems.length === 1}
                      >
                        ×
                      </Button>
                    </div>
                  </div>
                ))}
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setQuoteItems([...quoteItems, { description: '', contractor_cost: '', quantity: 1 }])}
                >
                  <Plus className="h-3 w-3 mr-1" /> Add Line
                </Button>
              </div>

              {/* Price Summary - INTERNAL VIEW */}
              <Card className="bg-slate-50">
                <CardContent className="p-4">
                  <h4 className="font-medium text-sm mb-3 text-slate-700">Price Summary (Internal View)</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Contractor Cost:</span>
                      <span>£{calculateQuoteTotal().contractor}</span>
                    </div>
                    <div className="flex justify-between text-teal-600">
                      <span>PHS Margin (25%):</span>
                      <span className="font-medium">£{calculateQuoteTotal().margin}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold pt-2 border-t">
                      <span>Client Price:</span>
                      <span className="text-teal-700">£{calculateQuoteTotal().client}</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-3">
                    Client will only see: Total £{calculateQuoteTotal().client}
                  </p>
                </CardContent>
              </Card>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowQuoteDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleCreateQuote}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="confirm-create-quote-btn"
              >
                Create Quote (Draft)
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
